import * as React from "react";

import { Link } from "react-router-dom";

import {
  AccessReportsAdminVersionTypeEnum,
  CacheProviderSets,
  ManageOptions,
  SidePanelTypeEnum,
} from "@app/access-reports/admin/";
import {
  AccessReportsCoverPageElementEnum,
  AccessReportsWizardElement,
  AccessReportsWizardOption,
  AccessReportsWizardPage,
  AccessReportsWizardProduct,
  AccessReportsWizardStepEnum,
  AccessReportsWizardTemplate,
} from "@app/access-reports/shared";
import { getElementByIdFromTemplate } from "@app/access-reports/shared/utility";
import { QESAlert, QESButton } from "@app/shared/qes-standards";
import { ColumnConfig, TextAlignment, Waffles } from "@app/shared/waffles";

import { flatten, isEqual } from "lodash";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import { JSX } from "react";

interface AccessReportsAdminProductsTabProps {
  accessReportsWizardTemplate: AccessReportsWizardTemplate;
  disableUserActions: boolean;
  onRemoveProductsClick: (products: AccessReportsWizardProduct[]) => void;
  onSaveProductsClick: (productTypes: AccessReportsWizardOption[]) => void;
  onSaveProductTypesClick: (productTypes: AccessReportsWizardOption[]) => void;
  selectedVersionType: AccessReportsAdminVersionTypeEnum;
}

interface AccessReportsAdminProductsTabState {
  products: AccessReportsWizardProduct[];
  selectedProducts: AccessReportsWizardProduct[];
  showRemoveProductModal: boolean;
  visibleSidePanel: SidePanelTypeEnum;
}

class AccessReportsAdminProductsWaffleToppings {
  public edit: JSX.Element;
  public key: string;
  public groups: number;
  public product: string;
  public productType: string;
  public specialties: number;

  constructor(
    key: string,
    product: string,
    productType: string,
    specialties: number,
    groups: number,
    edit: JSX.Element,
  ) {
    /* eslint-disable sort-keys */
    this.key = key;
    this.product = product;
    this.productType = productType;
    this.specialties = specialties;
    this.groups = groups;
    this.edit = edit;
    /* eslint-enable sort-keys */
  }
}

class AccessReportsAdminProductsTab extends React.Component<AccessReportsAdminProductsTabProps, AccessReportsAdminProductsTabState> {
  private deleteButtonRef = React.createRef<HTMLButtonElement>();

  constructor(props: AccessReportsAdminProductsTabProps) {
    super(props);

    this.state = {
      products: [],
      selectedProducts: [],
      showRemoveProductModal: false,
      visibleSidePanel: null,
    };
  }

  public componentDidMount() {
    if (!!this.props.accessReportsWizardTemplate) {
      this.setProducts();
    }
  }

  public componentDidUpdate(prevProps: AccessReportsAdminProductsTabProps) {
    if (!isEqual(prevProps.accessReportsWizardTemplate, this.props.accessReportsWizardTemplate)) {
      this.setProducts();
    }
  }

  public render() {
    return (
      <div className="tab-pane fade active show" id="products">
        <Modal
          isVisible={this.state.showRemoveProductModal}
          content={{
            body: (
              <div className="overflow-auto" >
                <p>
                  Are you sure you want to delete the following products:
                </p>
                <ul>
                  {this.state.selectedProducts.map((selectedProduct, index) => (
                    <li className="mb-0" key={index}>
                      {selectedProduct.displayName ? selectedProduct.displayName : selectedProduct.name}
                    </li>
                  ))}
                </ul>
              </div>
            ),
            cancelButtonText: "Cancel",
            confirmButtonAction: () => this.hideRemoveProductAlert(true),
            confirmButtonText: "Delete",
            confirmButtonType: "primary-danger",
            title: "Warning",
          }}
          closeModal={() => this.hideRemoveProductAlert(false)}
          hideBodyScroll={true}
          triggerElement={this.deleteButtonRef}
        />

        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between">
              <div>
                <FlyoutLink
                  classNames="qes-button-secondary ml-3 mb-3"
                  content={{
                    body: this.renderManageProductTypesSidePanel(),
                    title: `${this.props.disableUserActions ? "View" : "Manage"} Product Types`,
                  }}
                  panelId="access-reports-admin__products--manage-product-types"
                  isVisible={this.state.visibleSidePanel === SidePanelTypeEnum.ManageProductTypes}
                  size="20"
                  testId="access-reports-admin__products--manage-product-types-link"
                  onClose={this.hideSidePanel}
                >
                  {this.props.disableUserActions ? "View" : "Manage"} Product Types
                </FlyoutLink>
                <span className="mx-3">
                  |
                </span>
                <FlyoutLink
                  classNames="qes-button-secondary mb-3"
                  content={{
                    body: this.renderManageProductsSidePanel(),
                    title: `${this.props.disableUserActions ? "View" : "Reorder"} Products`,
                  }}
                  panelId="access-reports-admin__products--manage-products"
                  isVisible={this.state.visibleSidePanel === SidePanelTypeEnum.ReorderProducts}
                  size="20"
                  testId="access-reports-admin__products--manage-products-link"
                  onClose={this.hideSidePanel}
                >
                  {this.props.disableUserActions ? "View" : "Reorder"} Products
                </FlyoutLink>
                {this.props.selectedVersionType === AccessReportsAdminVersionTypeEnum.CurrentVersion && (
                  <>
                    <span className="mx-3">
                      |
                    </span>
                    <FlyoutLink
                      classNames="qes-button-secondary mb-3"
                      content={{
                        body: this.renderCacheProviderSetsSidePanel(),
                        title: "Cache Provider Sets",
                      }}
                      panelId="access-reports-admin__products--cache-provider-sets"
                      isVisible={this.state.visibleSidePanel === SidePanelTypeEnum.CacheProviderSets}
                      size="40"
                      testId="access-reports-admin__products--cache-provider-sets-link"
                      onClose={this.hideSidePanel}
                    >
                      {this.props.disableUserActions ? "View" : "Cache"} Provider Sets
                    </FlyoutLink>
                  </>
                )}
              </div>

              {!this.props.disableUserActions && (
                this.getAddProductLink()
              )}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            {this.state.products.length ?
              <div id="access-reports-admin__products--container" className="mb-3">
                <Waffles<AccessReportsAdminProductsWaffleToppings>
                  bulkActionBar={!this.props.disableUserActions && this.getBulkActionBar()}
                  columns={this.getColumnConfig()}
                  data={this.buildWaffleToppings(this.state.products) || []}
                  fullWidth={true}
                  handleCheckboxChange={this.handleCheckboxChange}
                  selectedKeys={this.state.selectedProducts.map((p: AccessReportsWizardProduct) => p.id.toString())}
                  showCheckboxes={!this.props.disableUserActions}
                  testId="access-reports-admin__products--all-products"
                />
              </div> :
              <p className="text-center mt-3">
                No products have been defined.
              </p>
            }
          </div>
        </div>
      </div>
    );
  }

  private getAddProductLink = (): JSX.Element => {
    const productElement: AccessReportsWizardElement = getElementByIdFromTemplate(this.props.accessReportsWizardTemplate, AccessReportsWizardStepEnum.CoverPage, AccessReportsCoverPageElementEnum.Product);
    const id: number = !!productElement?.options.length ? Math.max(...productElement.options.map((o) => o.id)) + 1 : 0;

    return (
      <Link
        className="qes-button qes-button-secondary float-right mr-3"
        id="access-reports-admin__products--add-product"
        to={{
          pathname: `/access-reports/admin/product/${id}/step/2`,
        }}
      >
        Add Product
      </Link>
    );
  }

  private getBulkActionBar = (): JSX.Element => {
    return (
      <span>
        <QESButton
          className="p-1"
          disabled={this.props.disableUserActions || this.state.selectedProducts.length < 1}
          qesTestId="access-reports-admin__products--delete-products"
          qesType="secondary-danger"
          onClick={() => this.displayRemoveProductAlert()}
          qesRef={this.deleteButtonRef}
        >
          <i className="fa fa-trash fa-fw" /> Delete Selected Products
        </QESButton>
      </span>
    );
  }

  private getProductTypeProductConnections = (productTypeId: number): JSX.Element => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = this.props.accessReportsWizardTemplate;
    const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate.pages.find(
      x => x.title === AccessReportsWizardStepEnum.CoverPage
    );
    const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
    const productElement: AccessReportsWizardElement = coverPagePageElements.find(
      x => x.elementId === AccessReportsCoverPageElementEnum.Product
    );

    let alert: JSX.Element = null;

    if (!!productElement && !!productElement.options) {
      const connectedOptions = productElement.options.filter(
        x => x.previousElementFilterId !== null && x.previousElementFilterId !== undefined && x.previousElementFilterId === productTypeId
      );

      if (connectedOptions?.length) {
        alert = (
          <QESAlert classNames="mt-3 mb-3" type="danger" persist={true}>
            Please remove the connection to the following products:
            <ul className="mb-0">
              {connectedOptions.map((connectedOption, connectedOptionIndex) => (
                <li className="mb-0" key={connectedOptionIndex}>
                  {connectedOption.displayName ? connectedOption.displayName : connectedOption.name}
                </li>
              ))}
            </ul>
          </QESAlert>
        );
      }
    }

    return alert;
  }

  public handleCheckboxChange = (selectedKeys: string[]): void => {
    const selectedProducts: AccessReportsWizardProduct[] = this.state.products.filter((p) => selectedKeys.includes(p.id.toString()));

    this.setState({
      selectedProducts,
    });
  }

  public hideSidePanel = (): void => {
    if (this.state.visibleSidePanel) {
      this.setState({
        visibleSidePanel: null,
      });
    }
  }

  public onSaveProductsClick = (productTypes: AccessReportsWizardOption[]): void => {
    this.props.onSaveProductsClick(productTypes);
    this.hideSidePanel();
  }

  public onSaveProductTypesClick = (productTypes: AccessReportsWizardOption[]): void => {
    this.props.onSaveProductTypesClick(productTypes);
    this.hideSidePanel();
  }

  private buildWaffleToppings = (products: AccessReportsWizardProduct[]): AccessReportsAdminProductsWaffleToppings[] => {
    const waffleToppings: AccessReportsAdminProductsWaffleToppings[] = products && products.map((p, index) => {
      const key: string = p.id.toString();

      const product: string = p.displayName ? p.displayName : p.name;

      const productTypeString: string = this.getProductType(p);
      const productType: string = productTypeString ? productTypeString : "--";

      const specialties: number = p.accessCriterion.length;

      const groups: number = this.getProductGroupCount(p);

      const edit: JSX.Element = (
        <Link
          className="font-weight-bold"
          id={`access-reports-admin__products--edit-${index}`}
          to={{
            pathname: `/access-reports/admin/product/${p.id}/step/1`,
          }}
        >
          <QESButton
            qesType="secondary"
            className="p-1"
            qesTestId={`access-reports-admin__products--edit-icon-${index}`}
            title={this.props.disableUserActions ? "View" : "Edit"}
          >
            <i
              className={`fa fa-${this.props.disableUserActions ? "eye" : "pencil-square-o"} fa-fw`}
            />
          </QESButton>
        </Link>
      );

      return new AccessReportsAdminProductsWaffleToppings(key, product, productType, specialties, groups, edit);
    });

    return waffleToppings;
  }

  private displayRemoveProductAlert = (): void => {
    this.setState({
      showRemoveProductModal: true,
    });
  }

  private getColumnConfig = (): ColumnConfig => {
    const columns: ColumnConfig = {
      edit: {
        alignment: TextAlignment.Center,
        displayText: this.props.disableUserActions ? "View" : "Edit",
        noResize: true,
        noSort: true,
        testId: "edit",
      },
      groups: {
        displayText: "Groups",
        noResize: true,
        noSort: true,
        testId: "groups",
      },
      product: {
        displayText: "Product",
        noResize: true,
        noSort: true,
        testId: "product",
      },
      productType: {
        displayText: "Product Type",
        noResize: true,
        noSort: true,
        testId: "productType",
      },
      specialties: {
        displayText: "Specialties",
        noResize: true,
        noSort: true,
        testId: "specialties",
      },
    };

    return columns;
  }

  private getProductGroupCount = (product: AccessReportsWizardProduct): number => {
    const groups: string[] = [];

    for (const accessCriteria of product.accessCriterion) {
      if (accessCriteria.groups) {
        for (const group of accessCriteria.groups) {
          if (!groups.includes(group)) {
            groups.push(group);
          }
        }
      }
    }

    return groups.length;
  }

  private hideRemoveProductAlert = (removeSpecialtyConfirmed: boolean): void => {
    if (removeSpecialtyConfirmed) {
      this.props.onRemoveProductsClick(this.state.selectedProducts);
    }

    this.setState({
      showRemoveProductModal: false,
    });
  }

  private setProducts = (): void => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = this.props.accessReportsWizardTemplate;
    let products: AccessReportsWizardProduct[] = [];

    if (!!accessReportsWizardTemplate) {
      const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);

      if (!!coverPage) {
        const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage.formGroups.map(x => x.elements));
        const productElement: AccessReportsWizardElement = coverPagePageElements.find(
          x => x.elementId === AccessReportsCoverPageElementEnum.Product
        );

        if (!!productElement && !!productElement.options) {
          products = productElement.options as AccessReportsWizardProduct[];
        }
      }
    }

    this.setState({
      products,
    });
  }

  private getProductType = (product: AccessReportsWizardProduct): string => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = this.props.accessReportsWizardTemplate;

    if (!!accessReportsWizardTemplate) {
      const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);

      if (!!coverPage) {
        const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage.formGroups.map(x => x.elements));
        const productTypeElement: AccessReportsWizardElement = coverPagePageElements.find(
          x => x.elementId === AccessReportsCoverPageElementEnum.ProductType
        );

        if (!!productTypeElement && !!productTypeElement.options) {
          const productTypeOption: AccessReportsWizardOption = productTypeElement.options.find(x => x.id === product.previousElementFilterId);

          if (!!productTypeOption) {
            return productTypeOption.displayName ? productTypeOption.displayName : productTypeOption.name;
          }
        }
      }
    }
  }

  public renderCacheProviderSetsSidePanel = (): JSX.Element => {
    return (
      <CacheProviderSets
        hideSidePanel={this.hideSidePanel}
        subscribe={undefined}
        unsubscribe={undefined}
      />
    );
  }

  public renderManageProductsSidePanel = (): JSX.Element => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = this.props.accessReportsWizardTemplate;
    const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate?.pages.find(
      x => x.title === AccessReportsWizardStepEnum.CoverPage
    );
    const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
    const productElement: AccessReportsWizardElement = coverPagePageElements?.find(
      x => x.elementId === AccessReportsCoverPageElementEnum.Product
    );

    if (!productElement || !productElement.options) {
      console.error("Unable to retrieve Reorder Products Side Panel");
    }

    return (
      <ManageOptions
        disableAddingNewOptions={true}
        disableDeletion={true}
        disableNameEdit={true}
        disableUserActions={this.props.disableUserActions}
        getOptionConnections={this.getProductTypeProductConnections}
        onSaveOptionsClick={this.onSaveProductsClick}
        options={productElement.options}
        optionType={AccessReportsCoverPageElementEnum.Product}
        requireMinimumOne={true}
      />
    );
  }

  public renderManageProductTypesSidePanel = (): JSX.Element => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = this.props.accessReportsWizardTemplate;
    const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate?.pages.find(
      x => x.title === AccessReportsWizardStepEnum.CoverPage
    );
    const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
    const productTypeElement: AccessReportsWizardElement = coverPagePageElements?.find(
      x => x.elementId === AccessReportsCoverPageElementEnum.ProductType
    );

    if (!productTypeElement || !productTypeElement.options) {
      console.error("Unable to retrieve Manage Product Types Side Panel");
    }

    return (
      <ManageOptions
        disableUserActions={this.props.disableUserActions}
        getOptionConnections={this.getProductTypeProductConnections}
        onSaveOptionsClick={this.onSaveProductTypesClick}
        options={productTypeElement.options}
        optionType={this.splitKey(AccessReportsCoverPageElementEnum.ProductType)}
        requireMinimumOne={true}
      />
    );
  }

  private splitKey = (key: string): string => {
    const splitKey: string = key.replace(/([A-Z])/g, " $1");
    const formattedKey: string = (splitKey.charAt(0).toUpperCase() + splitKey.slice(1));

    return formattedKey;
  }
}

export { AccessReportsAdminProductsTab };
